import * as React from "react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { IconX } from "@tabler/icons-react";
import { MButton } from "./MButtons";
import { MInputField } from "./MInputField";
import styles from "./MDialog.module.css";

export interface MDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  titleVariant?: 'default' | 'sm';
  description?: React.ReactNode;
  children?: React.ReactNode;
  primaryCtaText?: string;
  onPrimaryCta?: () => void;
  secondaryCtaText?: string;
  onSecondaryCta?: () => void;
  isPrimaryCtaLoading?: boolean;
  isPrimaryCtaDisabled?: boolean;
  showPrimaryCta?: boolean;
  showCloseButton?: boolean;
  className?: string;
  destructivePrimary?: boolean;
  disableEscapeKey?: boolean;
  disableOutsideClick?: boolean;
  icon?: React.ReactNode;
  showIconVariant?: boolean;
  showInputVariant?: boolean;
  inputPlaceholder?: string;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  inputType?: 'email' | 'tel' | 'text';
  inputLabel?: string;
  showLabel?: boolean;
  showDialCode?: boolean;
  dialCode?: string;
  onDialCodeClick?: () => void;
}

export const MDialog: React.FC<MDialogProps> = ({
  isOpen,
  onClose,
  title = '',
  titleVariant = 'default',
  description,
  children,
  primaryCtaText,
  onPrimaryCta,
  secondaryCtaText,
  onSecondaryCta,
  isPrimaryCtaLoading = false,
  isPrimaryCtaDisabled = false,
  showPrimaryCta = true,
  showCloseButton = true,
  className,
  destructivePrimary = false,
  disableEscapeKey = false,
  disableOutsideClick = false,
  icon,
  showIconVariant = false,
  showInputVariant = false,
  inputPlaceholder,
  inputValue,
  onInputChange,
  inputType = 'text',
  inputLabel,
  showLabel = false,
  showDialCode = false,
  dialCode,
  onDialCodeClick,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        isOpen &&
        modalRef.current &&
        !modalRef.current.contains(e.target as Node) &&
        !disableOutsideClick
      ) {
        onClose();
      }
    }
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, onClose, disableOutsideClick]);

  // Close on Escape key
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (isOpen && e.key === "Escape" && !disableEscapeKey) onClose();
    }
    if (isOpen) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose, disableEscapeKey]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.mDialog__overlay}>
      <div
        ref={modalRef}
        className={[styles.mDialog, showIconVariant ? styles.mDialog__iconVariant : '', className].filter(Boolean).join(" ")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mDialogTitle"
        aria-describedby="mDialogDesc"
        tabIndex={-1}
      >
        {showIconVariant && icon && (
          <div className={styles.mDialog__iconContainer}>
            {icon}
          </div>
        )}
        <div className={styles.mDialog__header}>
          {!showIconVariant && !showInputVariant && (
            <div
              className={styles.mDialog__title}
              id="mDialogTitle"
              style={titleVariant === 'sm' ? { fontSize: 16 } : undefined}
            >
              {title}
            </div>
          )}
          {showCloseButton && (
            <button
              onClick={onClose}
              aria-label="Close"
              className={styles.mDialog__close}
            >
              <IconX size={20} color="#2A2A2A" />
            </button>
          )}
        </div>
        {showIconVariant && !showInputVariant && (
          <div
            className={styles.mDialog__title}
            id="mDialogTitle"
            style={titleVariant === 'sm' ? { fontSize: 16 } : undefined}
          >
            {title}
          </div>
        )}
        {description && !showInputVariant && (
          <div className={styles.mDialog__content} id="mDialogDesc">
            {description}
          </div>
        )}
        {showInputVariant && (
          <div className={styles.mDialog__inputContainer}>
            {showDialCode ? (
              <div className={styles.mDialog__mobileInputContainer}>
                <div className={styles.mDialog__dialCodeStatic}>
                  <span>{dialCode}</span>
                </div>
                <MInputField
                  type={inputType}
                  placeholder={inputPlaceholder}
                  value={inputValue}
                  onChange={(e) => onInputChange?.(e.target.value)}
                  variant="Underlined"
                  className={styles.mDialog__mobileInputField}
                />
              </div>
            ) : (
              <MInputField
                type={inputType}
                placeholder={inputPlaceholder}
                value={inputValue}
                onChange={(e) => onInputChange?.(e.target.value)}
                variant="Underlined"
                className={styles.mDialog__inputField}
                label={inputLabel}
                showLabel={showLabel}
              />
            )}
          </div>
        )}
        {children && (
          <div
            className={styles.mDialog__children}
            style={((secondaryCtaText && onSecondaryCta) || (showPrimaryCta !== false && primaryCtaText && onPrimaryCta)) ? undefined : { marginBottom: 0 }}
          >
            {children}
          </div>
        )}
        {((secondaryCtaText && onSecondaryCta) || (showPrimaryCta !== false && primaryCtaText && onPrimaryCta)) && (
          <div className={styles.mDialog__actions}>
            {secondaryCtaText && onSecondaryCta && (
              <MButton
                variant="outline"
                size="md"
                className={styles.mDialog__ctaLeft}
                onClick={onSecondaryCta}
              >
                {secondaryCtaText}
              </MButton>
            )}
            {showPrimaryCta !== false && primaryCtaText && onPrimaryCta && (
              <MButton
                variant={destructivePrimary ? "destructive" : "filled"}
                size="md"
                className={styles.mDialog__ctaRight}
                onClick={onPrimaryCta}
                disabled={isPrimaryCtaDisabled || isPrimaryCtaLoading}
              >
                {primaryCtaText}
              </MButton>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MDialog;
